import { chacha20poly1305 } from "@noble/ciphers/chacha";
import { randomBytes } from "@noble/ciphers/webcrypto";

/**
 * Authenticated symmetric encryption with ChaCha20-Poly1305.
 *
 * Layout of the returned ciphertext:
 *   [12-byte nonce][ciphertext+tag]
 *
 * The nonce is freshly generated on each call. Reusing a (key, nonce)
 * pair is catastrophic — never derive nonces from message content.
 */
export interface SealedBlob {
  /** Concatenated [nonce | ciphertext | tag], base64url-encoded. */
  ct: string;
}

const NONCE_LEN = 12;

const enc = new TextEncoder();
const dec = new TextDecoder();

export function seal(plaintext: string, key: Uint8Array): SealedBlob {
  if (key.length !== 32) throw new Error("seal: key must be 32 bytes");
  const nonce = randomBytes(NONCE_LEN);
  const ct = chacha20poly1305(key, nonce).encrypt(enc.encode(plaintext));
  const out = new Uint8Array(nonce.length + ct.length);
  out.set(nonce, 0);
  out.set(ct, nonce.length);
  return { ct: b64uEncode(out) };
}

export function open(blob: SealedBlob, key: Uint8Array): string {
  if (key.length !== 32) throw new Error("open: key must be 32 bytes");
  const buf = b64uDecode(blob.ct);
  if (buf.length < NONCE_LEN + 16)
    throw new Error("open: ciphertext too short");
  const nonce = buf.slice(0, NONCE_LEN);
  const ct = buf.slice(NONCE_LEN);
  const pt = chacha20poly1305(key, nonce).decrypt(ct);
  return dec.decode(pt);
}

/** Constant-time equality for two byte arrays. */
export function ctEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let r = 0;
  for (let i = 0; i < a.length; i++) r |= a[i]! ^ b[i]!;
  return r === 0;
}

// ---- base64url helpers ------------------------------------------------

export function b64uEncode(buf: Uint8Array): string {
  let s = "";
  for (let i = 0; i < buf.length; i++) s += String.fromCharCode(buf[i]!);
  return btoa(s).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

export function b64uDecode(str: string): Uint8Array {
  const pad = "=".repeat((4 - (str.length % 4)) % 4);
  const s = atob(str.replaceAll("-", "+").replaceAll("_", "/") + pad);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}
